<script lang="ts">
	import { TalksRoutes } from '../../constants/routes';
	import { MobilePodcastEpisodeCard } from './MobilePodcastEpisodeCard';
	import { ApplePodcastsIcon } from '$global/assets/icons/ApplePodcastsIcon';
	import { SpotifyIcon } from '$global/assets/icons/SpotifyIcon';
	import { YouTubeIcon } from '$global/assets/icons/YouTubeIcon';
	import { Markdown } from '$global/components/Markdown';
	import { Txt } from '$global/components/Txt';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { track } from '$global/utils/track';
	import type { PodcastPageData } from '$routes/talks/podcasts/[slug]/types';

	interface Props {
		podcastEpisode: NonNullable<PodcastPageData['podcast']['episodes']>[number];
		seriesAndDate?: string;
	}

	let { podcastEpisode, seriesAndDate }: Props = $props();

	const dataCyPrefix = 'podcast-episode-card';
</script>

<MobilePodcastEpisodeCard {podcastEpisode} {seriesAndDate} class="md:hidden" />

<a
	href={`${TalksRoutes.Podcasts}/${podcastEpisode?.podcast?.slug}/episodes/${podcastEpisode?.slug}`}
	class="hidden h-full max-w-[850px] gap-4 border border-gray-200 bg-white p-4 pr-12 md:flex"
	onclick={() => {
		track('TALKS/PODCAST_PAGE_EPISODE_LINK_CLICK', {
			slug: podcastEpisode?.slug,
		});
	}}
>
	<img
		alt={podcastEpisode?.title}
		src={getImageUrl(podcastEpisode?.image?.filename_disk)}
		class="h-[140px] max-w-[246px] min-w-[246px]"
		data-cy={`${dataCyPrefix}-image`}
	/>
	<div class="flex grow flex-col justify-between">
		<div>
			<Txt variant="h5" dataCy={`${dataCyPrefix}-title`}>
				{podcastEpisode?.title}
			</Txt>
			{#if seriesAndDate}
				<Txt
					variant="label4"
					class="mb-2 text-gray-500"
					dataCy={`${dataCyPrefix}-series-and-date`}
				>
					{seriesAndDate}
				</Txt>
			{/if}
			<Markdown
				dataCy={`${dataCyPrefix}-description`}
				source={podcastEpisode.shortDescriptionTokens}
				class="line-clamp-2"
			/>
		</div>
		<div class="flex items-end justify-between">
			<Txt class="underline">View episode</Txt>

			<div class="mt-6 flex gap-3">
				{#if podcastEpisode?.youtube_link}
					<button
						data-cy={`${dataCyPrefix}-youtube`}
						onclick={(e) => {
							e.preventDefault();

							track('TALKS/PODCAST_PAGE_EPISODE_YOUTUBE_CLICK', {
								slug: podcastEpisode?.slug,
							});

							window.open(
								podcastEpisode?.youtube_link,
								'_blank',
								'noopener noreferrer'
							);
						}}
					>
						<YouTubeIcon class="scale-[1.3]" hexCode="#FF0000" />
					</button>
				{/if}
				{#if podcastEpisode?.spotify_link}
					<button
						data-cy={`${dataCyPrefix}-spotify`}
						onclick={(e) => {
							e.preventDefault();

							track('TALKS/PODCAST_PAGE_EPISODE_SPOTIFY_CLICK', {
								slug: podcastEpisode?.slug,
							});

							window.open(
								podcastEpisode?.spotify_link as string,
								'_blank',
								'noopener noreferrer'
							);
						}}
					>
						<SpotifyIcon />
					</button>
				{/if}
				{#if podcastEpisode?.apple_podcasts_link}
					<button
						data-cy={`${dataCyPrefix}-apple-podcasts`}
						onclick={(e) => {
							e.preventDefault();

							track('TALKS/PODCAST_PAGE_EPISODE_APPLE_CLICK', {
								slug: podcastEpisode?.slug,
							});

							window.open(
								podcastEpisode?.apple_podcasts_link as string,
								'_blank',
								'noopener noreferrer'
							);
						}}
					>
						<ApplePodcastsIcon />
					</button>
				{/if}
			</div>
		</div>
	</div>
</a>
